import { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useUserRole } from '@/hooks/useUserRole';
import { useAuth } from '@/hooks/useAuth';
import { LayoutWrapper } from './LayoutWrapper';
import { PublicTopNav } from '@/components/navigation/PublicTopNav';

interface ConditionalLayoutProps {
  children: ReactNode;
  publicComponent?: ReactNode; // Component to show when not logged in
}

/**
 * Conditionally wraps children in role-specific layouts when logged in,
 * or shows public navigation when not logged in.
 * 
 * NOTE: Only use this for PUBLIC routes. Routes under /buyer/*, /supplier/*, /admin/*
 * are already wrapped in their respective layouts and should NOT use this component.
 */
export function ConditionalLayout({ children, publicComponent }: ConditionalLayoutProps) {
  const { role, loading: roleLoading } = useUserRole();
  const { user, roles } = useAuth();
  const location = useLocation();

  // Check if we're already in a layout route (these already have sidebars)
  // Exclude public profile routes like /supplier/:id and /buyer/:id which should use ConditionalLayout
  const isPublicProfileRoute = 
    location.pathname.match(/^\/supplier\/[^/]+$/) ||
    location.pathname.match(/^\/buyer\/[^/]+$/) ||
    location.pathname.match(/^\/suppliers\/[^/]+$/);
  
  const isInLayoutRoute = 
    (location.pathname.startsWith('/buyer/') ||
     location.pathname.startsWith('/supplier/') ||
     location.pathname.startsWith('/admin/')) &&
    !isPublicProfileRoute;

  // If already in a layout route, just return children (don't double-wrap)
  if (isInLayoutRoute) {
    return <>{children}</>;
  }

  // OPTIMIZED: Use cached roles from AuthContext if available (faster than waiting for useUserRole)
  // This prevents flash from PublicTopNav -> LayoutWrapper transition
  // If we have roles from AuthContext, use them immediately even if useUserRole is still loading
  const hasCachedRoles = roles.length > 0;
  const cachedRole = hasCachedRoles 
    ? (roles.includes('supplier') ? 'supplier' : roles.includes('buyer') ? 'buyer' : null)
    : null;

  // Use cached role if available, otherwise use role from useUserRole
  // Filter out 'admin' role since LayoutWrapper only accepts 'buyer' | 'supplier'
  const rawRole = cachedRole || role;
  const effectiveRole = rawRole === 'admin' ? null : (rawRole === 'buyer' || rawRole === 'supplier' ? rawRole : null);

  // If user is logged in and we have a valid role (cached or determined), use role-specific layout
  // For supplier profile pages, default to buyer layout since they're buyer-facing
  // IMPORTANT: Don't render layout until role is determined to avoid showing wrong portal
  if (user && effectiveRole) {
    // For supplier profile pages, use buyer layout
    if (isPublicProfileRoute && location.pathname.match(/^\/supplier\/[^/]+$|^\/suppliers\/[^/]+$/)) {
      return <LayoutWrapper role="buyer">{children}</LayoutWrapper>;
    }
    // Use the determined role - ensure it's 'buyer' or 'supplier'
    return <LayoutWrapper role={effectiveRole}>{children}</LayoutWrapper>;
  }
  
  // If user is logged in but role is still loading, show public nav to avoid wrong layout
  if (user && roleLoading) {
    return (
      <>
        <PublicTopNav />
        {publicComponent || children}
      </>
    );
  }

  // If not logged in or no role, show public navigation
  return (
    <>
      <PublicTopNav />
      {publicComponent || children}
    </>
  );
}

